import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { Table, Tag, Space, Input, Button, Switch } from "antd";
import { RefreshCcw } from "lucide-react";

type RecordItem = {
  chat_id: number;
  group_name: string;
  last_checkin_time: string | null;
  next_checkin_time: string | null;
  last_status: string | null;
  state: "active" | "paused" | "disabled" | "slow_mode";
  consecutive_failures: number;
};

type RecordsResponse = {
  success: boolean;
  total: number;
  page: number;
  page_size: number;
  records: RecordItem[];
};

type SummaryResponse = {
  success: boolean;
  total: number;
  by_status: Record<string, number>;
  by_state: Record<string, number>;
};

// 已移除状态/运行态下拉筛选

const statusVariant = (s?: string | null) => {
  switch (s) {
    case "success":
    case "text_only":
      return "success";
    case "write_forbidden":
      return "warning";
    case "account_write_forbidden":
      return "error";
    case "rate_limited":
    case "slow_mode":
      return "processing";
    case "not_in_group":
    case "group_banned":
    case "telegram_forbidden":
    case "invalid_peer":
    case "chat_restricted":
    case "topic_closed":
    case "payment_required":
    case "request_failed":
    case "other_error":
    case "unknown_error":
    default:
      return "default";
  }
};

const stateVariant = (s: string) => {
  switch (s) {
    case "active":
      return "success";
    case "paused":
    case "slow_mode":
      return "warning";
    case "disabled":
      return "error";
    default:
      return "default";
  }
};

const statusLabel = (s?: string | null) => {
  switch (s) {
    case "success":
      return "成功";
    case "text_only":
      return "仅文字";
    case "write_forbidden":
      return "群禁言";
    case "not_in_group":
      return "不在群";
    case "account_write_forbidden":
      return "账号受限";
    case "rate_limited":
      return "限流";
    case "slow_mode":
      return "群组慢速模式";
    case "group_banned":
      return "群组禁止该账号发言";
    case "telegram_forbidden":
      return "Telegram 拒绝发送";
    case "invalid_peer":
      return "发送目标无效";
    case "chat_restricted":
      return "群组受限";
    case "topic_closed":
      return "话题已关闭";
    case "payment_required":
      return "需要付费才能发送";
    case "request_failed":
      return "请求多次失败";
    case "other_error":
      return "其他错误";
    case "unknown_error":
      return "未知错误";
    default:
      return "—";
  }
};

const stateLabel = (s: string) => {
  switch (s) {
    case "active":
      return "活跃";
    case "paused":
      return "暂停";
    case "slow_mode":
      return "等待发言";
    case "disabled":
      return "已禁用";
    default:
      return s;
  }
};

export default function RecordsPanel() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  // 取消按状态/运行态筛选，仅保留“仅问题群”快捷筛选
  const [onlyProblem, setOnlyProblem] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["checkinRecords", q, onlyProblem, page, pageSize] });
      queryClient.invalidateQueries({ queryKey: ["checkinSummary"] });
    }, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, queryClient, q, onlyProblem, page, pageSize]);

  // 汇总
  const { data: summary } = useQuery<SummaryResponse>({
    queryKey: ["checkinSummary"],
    queryFn: async () => {
      const res = await api.get("/api/checkin/summary");
      return res.data as SummaryResponse;
    },
  });

  // 列表
  const { data: listData, isFetching } = useQuery<RecordsResponse>({
    queryKey: ["checkinRecords", q, onlyProblem, page, pageSize],
    queryFn: async () => {
      const statuses = new Set<string>();
      const states = new Set<string>();
      if (onlyProblem) {
        ["write_forbidden", "not_in_group", "account_write_forbidden", "rate_limited", "slow_mode", "group_banned", "telegram_forbidden", "invalid_peer", "chat_restricted", "topic_closed", "payment_required", "request_failed", "other_error", "unknown_error"].forEach((x) => statuses.add(x));
        ["paused", "disabled", "slow_mode"].forEach((x) => states.add(x));
      }
      const sp = new URLSearchParams();
      sp.set("page", String(page));
      sp.set("page_size", String(pageSize));
      if (q.trim()) sp.set("q", q.trim());
      Array.from(statuses).forEach((s) => sp.append("status", s));
      Array.from(states).forEach((s) => sp.append("state", s));

      const res = await api.get(`/api/get_checkin_records?${sp.toString()}`);
      return res.data as RecordsResponse;
    },
    placeholderData: (prev) => prev as RecordsResponse,
  });

  const records: RecordItem[] = listData?.records || [];
  const total: number = listData?.total || 0;

  const columns: any[] = [
    {
      title: "群组",
      dataIndex: "group_name",
      key: "group_name",
      render: (text: string) => <span className="font-semibold">{text}</span>,
    },
    {
      title: "打卡状态",
      dataIndex: "last_status",
      key: "last_status",
      render: (s: string | null) => (
        <Tag className={`tag-soft tag-${statusVariant(s)}`}>{statusLabel(s)}</Tag>
      ),
    },
    {
      title: "活跃状态",
      dataIndex: "state",
      key: "state",
      render: (s: string) => (
        <Tag className={`tag-soft tag-${stateVariant(s)}`}>{stateLabel(s)}</Tag>
      ),
    },
    {
      title: "连续失败",
      dataIndex: "consecutive_failures",
      key: "consecutive_failures",
      render: (n: number) => (
        <span className={n > 0 ? "user-fail-count" : "user-muted"}>{n}</span>
      ),
      sorter: (a: RecordItem, b: RecordItem) => a.consecutive_failures - b.consecutive_failures,
    },
    {
      title: "上次打卡",
      dataIndex: "last_checkin_time",
      key: "last_checkin_time",
      render: (t: string | null) => t ? new Date(t).toLocaleString() : "—",
    },
    {
      title: "下次打卡",
      dataIndex: "next_checkin_time",
      key: "next_checkin_time",
      render: (t: string | null) => t ? new Date(t).toLocaleString() : "—",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, row: RecordItem) => (
        <Space>
          {row.state === "paused" && (
            <Button
              size="small"
              type="primary"
              onClick={async () => {
                try {
                  await api.post("/api/resume_group", null, { params: { chat_id: row.chat_id } });
                  queryClient.invalidateQueries({ queryKey: ["checkinRecords"] });
                  queryClient.invalidateQueries({ queryKey: ["checkinSummary"] });
                } catch (e) {
                  // 忽略恢复操作失败，由后续刷新展示当前状态。
                }
              }}
            >
              恢复
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["checkinRecords"] });
    queryClient.invalidateQueries({ queryKey: ["checkinSummary"] });
  };

  return (
    <div className="user-stack user-records">
      {/* 精简摘要：成功群组 / 暂停群组 / 失败群组 */}
      <div className="user-record-summary">
        {(() => {
          const byStatus = summary?.by_status || {};
          const byState = (summary?.by_state || {}) as Record<string, number>;
          const successCount = (byStatus["success"] || 0) + (byStatus["text_only"] || 0);
          const pausedCount = byState["paused"] || 0;
          const failKeys = [
            "write_forbidden",
            "not_in_group",
            "account_write_forbidden",
            "rate_limited",
            "slow_mode",
            "group_banned",
            "telegram_forbidden",
            "invalid_peer",
            "chat_restricted",
            "topic_closed",
            "payment_required",
            "request_failed",
            "other_error",
            "unknown_error",
          ];
          const failedCount = failKeys.reduce((acc, k) => acc + (byStatus[k] || 0), 0);
          const items = [
            { label: "成功群组", value: successCount, color: "is-success" },
            { label: "暂停群组", value: pausedCount, color: "is-warning" },
            { label: "失败群组", value: failedCount, color: "is-danger" },
          ];
          return items.map((it) => (
            <div key={it.label} className={`user-record-stat ${it.color}`}>
              <span>{it.label}</span>
              <strong>{it.value}</strong>
            </div>
          ));
        })()}
      </div>

      {/* 工具条 */}
      <div className="user-card user-record-table-card">
      <div className="user-card-heading"><h2>群组打卡记录</h2></div>
      <div className="user-record-toolbar">
        <Input
          allowClear
          placeholder="搜索群名"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-56"
        />
        <div className="flex items-center gap-1">
          <span>仅问题群</span>
          <Switch checked={onlyProblem} onChange={(v) => setOnlyProblem(v)} />
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Button onClick={handleRefresh} icon={<RefreshCcw className="w-4 h-4" /> as any}>刷新</Button>
          <div className="flex items-center gap-1">
            <span>自动刷新</span>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          </div>
        </div>
      </div>

      {/* 表格 */}
      <Table
        rowKey={(r) => `${r.chat_id}`}
        columns={columns}
        dataSource={records}
        loading={isFetching}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        className="user-record-table"
      />
      </div>
    </div>
  );
}
